import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from './config/supabase';
import { checkSupabaseConnection } from './config/supabase';
import { appService, gymnastService, levelService, eventService, generateSlug } from './services/supabaseService';
import { useNotifications } from './components/shared/useNotifications';
import { useConfirm } from './components/shared/ConfirmContext';
import { useAuth } from './context/AuthContext';
import Header from './components/shared/Header';
import Sidebar from './components/shared/Sidebar';
import CategoryFooter from './components/shared/CategoryFooter';
import PublicView from './views/PublicView';
import AdminView from './views/AdminView';
import ScoreEntryView from './views/ScoreEntryView';
import ClubsView from './views/ClubsView';
import JudgesView from './views/JudgesView';
import AnalyticsView from './views/AnalyticsView';
import LoginNew from './components/auth/LoginNew';
import JudgeRegistration from './components/auth/JudgeRegistration';
import { VIEWS, LEVEL_OPTIONS, FRANJA_OPTIONS, APPARATUS_OPTIONS } from './utils/constants';
import { AUTH_ROLES } from './config/authConfig';
import './App.css';
import './styles/shared.css';
import logger from './utils/logger';



const LoadingScreen = () => (
  <div className="loading-screen">
    <div style={{ textAlign: 'center' }}>
      <div className="spinner"></div>
      <p style={{ color: '#4b5563' }}>Cargando datos...</p>
    </div>
  </div>
);

const GymnasticsScoresApp = ({ initialRoute = 'public' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { success: notifySuccess, error: notifyError, warning: notifyWarning } = useNotifications();
  const confirm = useConfirm();
  const { isAuthenticated, role: authRole, signOut: authSignOut } = useAuth();
  
  // Determinar el rol inicial basado en la ruta
  const getInitialViewFromRoute = (pathname) => {
    if (pathname.includes('/admin')) return VIEWS.ADMIN;
    if (pathname.includes('/jueces')) return VIEWS.SCORES;
    if (pathname.includes('/resultados')) return VIEWS.PUBLIC;
    return VIEWS.PUBLIC;
  };
  
  const [view, setView] = useState(() => getInitialViewFromRoute(location.pathname));
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [judges, setJudges] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [gymnasts, setGymnasts] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [showJudgeRegistration, setShowJudgeRegistration] = useState(false);
  
  // Authentication state - derived from Supabase Auth context
  const [auth, setAuth] = useState(() => {
    // Legacy fallback removed — now driven by AuthContext
    return { role: null, user: null };
  });

  // Ref to track previous auth values and avoid infinite re-render loops
  const prevAuthRef = useRef({ role: null });

  // Sync auth state from AuthContext
  useEffect(() => {
    let nextRole = null;
    let nextUser = null;

    if (isAuthenticated && authRole === 'admin') {
      nextRole = AUTH_ROLES.ADMIN;
      nextUser = { role: 'admin' };
    } else if (location.pathname.startsWith('/resultados')) {
      nextRole = AUTH_ROLES.PUBLIC;
      nextUser = null;
    }

    // Only update state if role actually changed to prevent infinite loop
    if (prevAuthRef.current.role !== nextRole) {
      prevAuthRef.current = { role: nextRole };
      setAuth({ role: nextRole, user: nextUser });
    }
  }, [isAuthenticated, authRole, location.pathname]);
  
  // Actualizar vista cuando la ruta cambia
  useEffect(() => {
    const newView = getInitialViewFromRoute(location.pathname);
    setView(newView);
    
    // Redirigir a la vista predeterminada si no tiene permiso
    const renderAuth = (location.pathname.startsWith('/resultados') && (!auth.role || auth.role === AUTH_ROLES.PUBLIC))
      ? { role: AUTH_ROLES.PUBLIC, user: null }
      : auth;
      
    const allowedViewsForRole = renderAuth.role === AUTH_ROLES.ADMIN 
      ? [VIEWS.ADMIN, VIEWS.CLUBS, VIEWS.JUDGES, VIEWS.PUBLIC]
      : renderAuth.role === AUTH_ROLES.JUDGE
      ? [VIEWS.SCORES, VIEWS.PUBLIC]
      : [VIEWS.PUBLIC];
    
    if (!allowedViewsForRole.includes(newView)) {
      if (renderAuth.role === AUTH_ROLES.ADMIN) setView(VIEWS.ADMIN);
      else if (renderAuth.role === AUTH_ROLES.JUDGE) setView(VIEWS.SCORES);
      else setView(VIEWS.PUBLIC);
    }
  }, [location.pathname, auth.role, navigate]);

  const handleLoginSuccess = (authData) => {
    setAuth(authData);
    // Redirigir según el rol y la ruta
    if (authData.role === AUTH_ROLES.ADMIN) {
      setView(VIEWS.ADMIN);
      navigate('/admin');
    } else if (authData.role === AUTH_ROLES.JUDGE) {
      setView(VIEWS.SCORES);
      navigate('/jueces');
    } else {
      setView(VIEWS.PUBLIC);
      navigate('/');
    }
  };

  const handleLogout = async () => {
    await authSignOut();
    setAuth({ role: null, user: null });
    setView(VIEWS.PUBLIC);
    navigate('/');
  };

  const handleShowJudgeRegistration = () => {
    setShowJudgeRegistration(true);
  };

  const handleRegistrationSuccess = () => {
    setShowJudgeRegistration(false);
    setView(VIEWS.PUBLIC);
  };

  const handleChangeProfile = async () => {
    await authSignOut();
    setAuth({ role: null, user: null });
    // Redirigir siempre a la página de inicio (selector de roles)
    navigate('/');
  };
  
  // Proteger rutas que requieren autenticación
  useEffect(() => {
    // IMPORTANTE: NO redirigir si el usuario no está autenticado
    // Dejar que muestre el LoginNew
    if (!auth.role) {
      return;
    }
    
    // Solo redirigir si el usuario ESTÁ autenticado pero no tiene permiso para la ruta actual
    const currentPath = location.pathname;
    
    // Si usuario está en /admin pero no es ADMIN, redirigir
    if (currentPath.includes('/admin') && auth.role !== AUTH_ROLES.ADMIN) {
      if (auth.role === AUTH_ROLES.JUDGE) {
        navigate('/jueces');
      } else {
        navigate('/');
      }
    }
    // Si usuario está en /jueces pero no es JUDGE, redirigir
    else if (currentPath.includes('/jueces') && auth.role !== AUTH_ROLES.JUDGE) {
      if (auth.role === AUTH_ROLES.ADMIN) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    }
    // Si está en /resultados pero es PUBLIC, dejarlo (es correcto)
    // Si está en / (home), dejarlo
  }, [location.pathname, auth.role]);
  
  // Admin states
  const [showEventForm, setShowEventForm] = useState(false);
  const [showClubForm, setShowClubForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingClub, setEditingClub] = useState(null);
  const [expandedClubs, setExpandedClubs] = useState({});

  // Forms state
  const [eventForm, setEventForm] = useState({ 
    name: '', 
    date: '', 
    location: '', 
    discipline: '',
    category: '',
    apparatus: [],
    selectedLevels: [],
    imageFile: null, 
    imagePreview: null 
  });
  const [clubForm, setClubForm] = useState({ 
    name: '',
    event_id: '',
    gymnasts: [],
    imageFile: null,
    imagePreview: null
  });

  // Gymnast form state
  const [newGymnastForm, setNewGymnastForm] = useState({
    name: '',
    level: '',
    franja: '',
    imageFile: null,
    imagePreview: null
  });

  // Gymnast search state

  const [gymnastFilters, setGymnastFilters] = useState({
    level: '',
    franja: '',
    searchName: ''
  });

  // Editing state for gymnasts
  const [editingGymnast, setEditingGymnast] = useState(null);

  const [uploadingImage, setUploadingImage] = useState(false);

  // Gymnast search / modal state
  const [isSearching, setIsSearching] = useState(false);
  const [filteredGymnasts, setFilteredGymnasts] = useState([]);
  const [showGymnastModal, setShowGymnastModal] = useState(false);

  const currentEvent = events.find(e => e.id === selectedEvent);
  // Obtener clubes del evento actual (de la estructura del evento que ya tienen los clubes anidados)
  const currentClubs = Array.isArray(currentEvent?.clubs) ? currentEvent.clubs : [];

  // Auto-seleccionar evento cuando se accede por slug (/resultados/:slug)
  useEffect(() => {
    if (!slug || events.length === 0) return;
    // Buscar en los eventos ya cargados primero
    const found = events.find(e => e.slug === slug);
    if (found) {
      setSelectedEvent(found.id);
    } else {
      // Si no está en los eventos cargados, buscar en la base de datos
      eventService.getBySlug(slug).then(result => {
        if (result?.data) {
          setSelectedEvent(result.data.id);
        }
      });
    }
  }, [slug, events]);

  // Función para seleccionar evento y actualizar la URL
  const handleSelectEvent = async (eventId) => {
    if (eventId === null) {
      // Volver a la lista de competencias
      setSelectedEvent(null);
      navigate('/resultados');
      return;
    }

    setSelectedEvent(eventId);

    // Buscar el evento para obtener o generar el slug
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    let eventSlug = event.slug;

    // Si no tiene slug, generar uno y guardarlo en la DB
    if (!eventSlug) {
      const year = event.date ? new Date(event.date).getFullYear() : new Date().getFullYear();
      eventSlug = generateSlug(event.name) + '-' + year;

      // Guardar en la DB (fire-and-forget)
      try {
        const { error } = await supabase
          .from('events')
          .update({ slug: eventSlug })
          .eq('id', eventId);

        if (!error) {
          // Actualizar el evento en el estado local
          setEvents(prev => prev.map(e => e.id === eventId ? { ...e, slug: eventSlug } : e));
        }
      } catch (err) {
        // Silenciar error, navegar de todas formas
      }
    }

    navigate(`/resultados/${eventSlug}`, { replace: false });
  };

  // Solo inicializar datos cuando el usuario esté autenticado o en la vista pública
  useEffect(() => {
    const shouldLoadData = auth.role && auth.role !== AUTH_ROLES.PUBLIC;
    if (shouldLoadData && !loading) {
      initializeApp();
    } else if (location.pathname.startsWith('/resultados') && !loading) {
      // Para resultados (con o sin slug), también cargar datos
      initializeApp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.role, location.pathname]);

  const initializeApp = async () => {
    setLoading(true);
    const connection = await checkSupabaseConnection();
    setConnectionStatus(connection.connected ? 'connected' : 'disconnected');
    
    if (connection.connected) {
      await loadFromSupabase();
    } else {
      loadFromLocalStorage();
    }
    
    setLoading(false);
  };

  // Funciones principales
  const loadFromSupabase = async () => {
    try {
      const { events: eventsData, clubs: clubsData, judges: judgesData, error } = await appService.loadAllData();
      
      if (error) {
        throw error;
      }
      
      setEvents(eventsData);
      setClubs(clubsData); // Clubes globales sin relación con evento
      setJudges(judgesData); // Jueces globales
      
      // NO cargar gymnasts ni scores al inicio
      setGymnasts([]);
      setScores([]);
      
      // Solo seleccionar automáticamente evento si NO estamos en la vista pública
      // En PublicView, el usuario debe seleccionar manualmente (o se selecciona por slug)
      if (eventsData.length > 0 && !location.pathname.startsWith('/resultados')) {
        setSelectedEvent(eventsData[0].id);
      }
      
      saveToLocalStorage(eventsData, clubsData, judgesData);
      
      return true;
    } catch (error) {
      loadFromLocalStorage();
      return false;
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const eventsData = localStorage.getItem('gymnastics-events');
      const clubsData = localStorage.getItem('gymnastics-clubs');
      const judgesData = localStorage.getItem('gymnastics-judges');
      
      if (eventsData) {
        const parsedEvents = JSON.parse(eventsData);
        setEvents(parsedEvents);
        // Solo seleccionar automáticamente si NO estamos en la vista pública
        if (parsedEvents.length > 0 && !location.pathname.startsWith('/resultados')) {
          setSelectedEvent(parsedEvents[0].id);
        }
      }
      
      if (clubsData) {
        const parsedClubs = JSON.parse(clubsData);
        setClubs(parsedClubs);
      }

      if (judgesData) {
        const parsedJudges = JSON.parse(judgesData);
        setJudges(parsedJudges);
      }
    } catch (error) {
      setEvents([]);
      setClubs([]);
      setJudges([]);
    }
  };

  const saveToLocalStorage = (eventsData, clubsData, judgesData) => {
    try {
      localStorage.setItem('gymnastics-events', JSON.stringify(eventsData));
      localStorage.setItem('gymnastics-clubs', JSON.stringify(clubsData));
      localStorage.setItem('gymnastics-judges', JSON.stringify(judgesData));
    } catch (error) {
      logger.error('Error guardando en caché:', error?.message);
    }
  };

  const manualSync = async () => {
    setSyncing(true);
    try {
      await loadFromSupabase();
      notifySuccess('Datos sincronizados exitosamente');
    } catch (error) {
      notifyError('Error al sincronizar: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  // Cargar atletas cuando se selecciona un club
  const loadClubGymnasts = async (clubId) => {
    try {
      const { gymnasts: clubGymnasts, scores: clubScores, error } = await appService.loadClubGymnasts(clubId);
      
      if (error) {
        return;
      }

      setGymnasts(clubGymnasts);
      setScores(clubScores);
    } catch (error) {
      logger.error('Error cargando atletas del club:', error?.message);
    }
  };

  const exportData = () => {
    const data = {
      events,
      clubs,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `gymnastics-data-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (data.events && data.clubs) {
          const ok = await confirm({
            title: 'Importar Datos',
            message: '¿Estás seguro de importar estos datos? Se sobrescribirán los datos actuales.',
            type: 'warning',
            confirmText: 'Importar'
          });
          if (ok) {
            setEvents(data.events);
            setClubs(data.clubs);
            saveToLocalStorage(data.events, data.clubs);
            notifySuccess('Datos importados exitosamente');
          }
        } else {
          notifyError('El archivo no tiene el formato correcto');
        }
      } catch (error) {
        logger.error('Error importando datos:', error?.message);
        notifyError('Error al importar el archivo');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleFileChange = (e, setFormFunction) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        notifyError('Por favor selecciona un archivo de imagen válido (JPG, PNG, etc.)');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        notifyError('La imagen no debe superar los 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormFunction(prev => ({
          ...prev,
          imageFile: file,
          imagePreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImagePreview = (setFormFunction) => {
    setFormFunction(prev => ({
      ...prev,
      imageFile: null,
      imagePreview: null
    }));
  };

  // Función para cargar gimnastas del evento
  const loadEventGymnasts = async (filters = {}) => {
    if (!selectedEvent) return;
    
    setIsSearching(true);
    try {
      const searchFilters = {
        event_id: selectedEvent,
        ...filters
      };
      
      const result = await gymnastService.searchGymnasts(searchFilters);
      
      if (result.error) {
        logger.error('Error buscando gimnastas:', result.error?.message);
        notifyError('Error al buscar atletas');
        return;
      }
      
      // Obtener información adicional (club, imágenes)
      const gymnastsWithDetails = await Promise.all(
        (result.data || []).map(async (gymnast) => {
          // Obtener club
          const { data: club } = await supabase
            .from('clubs')
            .select('name')
            .eq('id', gymnast.club_id)
            .single();
          
          return {
            ...gymnast,
            club_name: club?.name || 'Sin club',
            total: parseFloat(gymnast.total) || 0,
            all_round: parseFloat(gymnast.all_round) || 0,
            scores: gymnast.scores || { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 }
          };
        })
      );
      
      setFilteredGymnasts(gymnastsWithDetails);
      
    } catch (error) {
      logger.error('Error cargando atletas:', error?.message);
      notifyError('Error al cargar atletas');
    } finally {
      setIsSearching(false);
    }
  };

  // Event functions
  const addEvent = async () => {
    if (!eventForm.name || !eventForm.date) {
      notifyWarning('Por favor completa nombre y fecha');
      return;
    }
    
    const result = await appService.saveEvent({
      id: editingEvent,
      name: eventForm.name,
      date: eventForm.date,
      location: eventForm.location || '',
      discipline: eventForm.discipline || '',
      category: eventForm.category || '',
      apparatus: eventForm.apparatus || [],
      selectedClubs: eventForm.selectedClubs || [],
      selectedJudges: eventForm.selectedJudges || [],
      is_active: eventForm.is_active !== false
    });
    
    if (result.success) {
      // Guardar niveles seleccionados después de que el evento sea creado/actualizado
      if (result.data?.id && eventForm.selectedLevels?.length > 0) {
        const levelResult = await levelService.setEventLevels(result.data.id, eventForm.selectedLevels);
        if (levelResult?.error) {
          logger.error('Error guardando niveles:', levelResult.error?.message);
          notifyError('Error guardando niveles: ' + levelResult.error?.message);
          return;
        }
      }
      
      if (eventForm.imageFile && result.data?.id) {
        await handleImageUpload('event', result.data.id, eventForm.imageFile);
      }
      
      await loadFromSupabase();
      
      setEventForm({ 
        name: '', 
        date: '', 
        location: '', 
        discipline: '',
        category: '',
        apparatus: [],
        selectedClubs: [],
        selectedJudges: [],
        selectedLevels: [],
        is_active: true,
        imageFile: null, 
        imagePreview: null 
      });
      setShowEventForm(false);
      setEditingEvent(null);
      
      notifySuccess(editingEvent ? 'Competencia actualizada exitosamente' : 'Competencia creada exitosamente');
    } else {
      notifyError('Error: ' + result.error);
    }
  };

  const deleteEvent = async (eventId) => {
    const ok = await confirm({
      title: 'Eliminar Competencia',
      message: '¿Estás seguro de eliminar esta competencia? También se eliminarán todos los clubes asociados.',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    
    const result = await appService.deleteEvent(eventId);
    
    if (result.success) {
      await loadFromSupabase();
      if (selectedEvent === eventId && events.length > 1) {
        setSelectedEvent(events[0].id);
      } else if (events.length === 1) {
        setSelectedEvent(null);
      }
      notifySuccess('Competencia eliminada exitosamente');
    } else {
      notifyError('Error: ' + result.error);
    }
  };

  const editEvent = async (event) => {
    setEditingEvent(event.id);
    
    // Cargar los niveles seleccionados para este evento
    const { data: eventLevels } = await levelService.getEventLevels(event.id);
    const selectedLevelIds = eventLevels?.map(l => l.level_id) || [];
    
    setEventForm({
      name: event.name,
      date: event.date,
      location: event.location || '',
      discipline: event.discipline || '',
      category: event.category || '',
      apparatus: event.apparatus || [],
      selectedClubs: (event.clubs || []).map(c => c.id),
      selectedJudges: (event.judges || []).map(j => j.id),
      selectedLevels: selectedLevelIds,
      is_active: event.is_active !== false,
      imageFile: null,
      imagePreview: event.image_url || null
    });
    setShowEventForm(true);
  };

  // Club functions
  const addClub = async () => {
    if (!clubForm.name || !selectedEvent) {
      notifyWarning('Por favor ingresa el nombre del club y selecciona una competencia');
      return;
    }
    
    // Solo guardar datos del club (nombre, ubicación, imagen)
    // Las atletas se manejan por separado con addGymnastToClubForm
    const clubData = {
      id: editingClub,
      name: clubForm.name,
      location: clubForm.location || '',
      event_id: selectedEvent,
      gymnasts: [] // No pasar atletas aquí, solo datos del club
    };
    
    const result = await appService.saveClub(clubData);
    
    if (result.success) {
      if (clubForm.imageFile && result.data?.id) {
        await handleImageUpload('club', result.data.id, clubForm.imageFile);
      }
      
      await loadFromSupabase();
      
      setClubForm({ 
        name: '',
        location: '',
        event_id: '',
        gymnasts: [],
        imageFile: null,
        imagePreview: null
      });
      setNewGymnastForm({ 
        name: '', 
        level: '', 
        franja: '',
        imageFile: null,
        imagePreview: null
      });
      setShowClubForm(false);
      setEditingClub(null);
      setEditingGymnast(null);
      
      notifySuccess(editingClub ? 'Club actualizado exitosamente' : 'Club creado exitosamente');
    } else {
      logger.error('Error guardando club:', result.error);
      notifyError('Error: ' + result.error);
    }
  };

  const deleteClub = async (clubId) => {
    const ok = await confirm({
      title: 'Eliminar Club',
      message: '¿Estás seguro de eliminar este club y todas sus atletas?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    
    const result = await appService.deleteClub(clubId);
    
    if (result.success) {
      await loadFromSupabase();
      const newExpandedClubs = { ...expandedClubs };
      delete newExpandedClubs[clubId];
      setExpandedClubs(newExpandedClubs);
      notifySuccess('Club eliminado exitosamente');
    } else {
      notifyError('Error: ' + result.error);
    }
  };

  const editClub = (club) => {
    setEditingClub(club.id);
    setClubForm({
      name: club.name,
      location: club.location || '',
      event_id: club.event_id,
      level: club.level || '',
      franja: club.franja || '',
      gymnasts: club.gymnasts.map(g => ({
        id: g.id,
        name: g.name,
        level: g.level,
        franja: g.franja,
        scores: g.scores,
        total: g.total,
        allRound: g.allRound,
        image_url: g.image_url || null
      })),
      imageFile: null,
      imagePreview: club.image_url || null
    });
    setShowClubForm(true);
  };

  const toggleExpandClub = (clubId) => {
    setExpandedClubs({
      ...expandedClubs,
      [clubId]: !expandedClubs[clubId]
    });
  };

  // Gymnast management in club form
  const addGymnastToClubForm = async () => {
    if (!newGymnastForm.name || !newGymnastForm.level || !newGymnastForm.franja) {
      notifyWarning('Por favor completa todos los campos de la atleta');
      return;
    }
    
    const newGymnast = {
      ...newGymnastForm,
      id: editingGymnast || `temp-${Date.now()}`,
      scores: { Suelo: 0, Viga: 0, Barras: 0, Salto: 0 },
      total: 0,
      allRound: 0,
      club_id: editingClub
    };
    
    try {
      // Si estamos editando una atleta existente, guardar en Supabase
      if (editingGymnast && !editingGymnast.startsWith('temp-')) {
        const result = await appService.saveGymnast(newGymnast);
        
        if (!result.success) {
          notifyError('Error al actualizar atleta: ' + result.error);
          return;
        }
        
        newGymnast.id = result.data.id;
      }
      // Si es una atleta nueva (sin temp-), guardar en Supabase
      else if (!editingGymnast || editingGymnast.startsWith('temp-')) {
        const result = await appService.saveGymnast(newGymnast);
        
        if (!result.success) {
          notifyError('Error al crear atleta: ' + result.error);
          return;
        }
        
        newGymnast.id = result.data.id;
      }
    } catch (error) {
      logger.error('Error saving gymnast:', error?.message);
      notifyError('Error al guardar atleta: ' + error.message);
      return;
    }
    
    if (editingGymnast) {
      const updatedGymnasts = clubForm.gymnasts.map(g => 
        g.id === editingGymnast ? newGymnast : g
      );
      setClubForm({
        ...clubForm,
        gymnasts: updatedGymnasts
      });
      setEditingGymnast(null);
    } else {
      setClubForm({
        ...clubForm,
        gymnasts: [...clubForm.gymnasts, newGymnast]
      });
    }
    
    setNewGymnastForm({
      name: '',
      level: '',
      franja: '',
      imageFile: null,
      imagePreview: null
    });
  };

  const removeGymnastFromClubForm = async (gymnastId) => {
    const ok = await confirm({
      title: 'Eliminar Atleta',
      message: '¿Estás seguro de eliminar esta atleta?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    
    // Eliminar de Supabase si no es temporal
    if (!gymnastId.startsWith('temp-')) {
      const result = await appService.deleteGymnast(gymnastId);
      
      if (!result.success) {
        notifyError('Error al eliminar atleta: ' + result.error);
        return;
      }
    }
    
    setClubForm({
      ...clubForm,
      gymnasts: clubForm.gymnasts.filter(g => g.id !== gymnastId)
    });
    
    if (editingGymnast === gymnastId) {
      setEditingGymnast(null);
      setNewGymnastForm({ 
        name: '', 
        level: '', 
            franja: '',
            imageFile: null,
            imagePreview: null
          });
        }
  };

  const editGymnastInForm = (gymnast) => {
    setEditingGymnast(gymnast.id);
    setNewGymnastForm({
      name: gymnast.name,
      level: gymnast.level,
      franja: gymnast.franja,
      imageFile: null,
      imagePreview: null
    });
  };

  // Función para manejar carga de imágenes
  const handleImageUpload = async (entityType, entityId, file) => {
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const result = await appService.uploadImage(entityType, entityId, file);
      
      if (result.success) {
        await loadFromSupabase();
        notifySuccess('Imagen subida exitosamente');
      } else {
        notifyError('Error al subir imagen: ' + result.error);
      }
    } catch (error) {
      logger.error('Error uploading image:', error?.message);
      notifyError('Error al subir imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  // Score functions
 const updateScore = async (clubId, gymnastId, apparatus, score) => {
  try {
    const club = clubs.find(c => c.id === clubId);
    if (!club) return;
    
    const gymnast = club.gymnasts.find(g => g.id === gymnastId);
    if (!gymnast) return;
    
    const newScores = { 
      ...gymnast.scores, 
      [apparatus]: parseFloat(score) || 0 
    };
    
    const scoresArray = Object.values(newScores);
    const total = scoresArray.reduce((sum, s) => sum + s, 0).toFixed(2);
    const allRound = (parseFloat(total) / scoresArray.length).toFixed(2);
    
    if (!gymnastId.startsWith('temp-')) {
      const result = await appService.updateGymnastScore(gymnastId, newScores);
      
      if (!result.success) {
        logger.error('Error updating score in Supabase:', result.error?.message);
      }
    }
    
    const updatedClubs = clubs.map(club => {
      if (club.id === clubId) {
        const updatedGymnasts = club.gymnasts.map(g => {
          if (g.id === gymnastId) {
            return {
              ...g,
              scores: newScores,
              total: parseFloat(total),
              allRound: parseFloat(allRound)
            };
          }
          return g;
        });
        
        return {
          ...club,
          gymnasts: updatedGymnasts
        };
      }
      return club;
    });
    
    setClubs(updatedClubs);
    saveToLocalStorage(events, updatedClubs);
    
  } catch (error) {
    logger.error('Error updating score:', error?.message);
    notifyError('Error al actualizar puntuación: ' + error.message);
  }
};

  const cancelEventForm = () => {
    setShowEventForm(false);
    setEditingEvent(null);
    setEventForm({ 
      name: '', 
      date: '', 
      location: '', 
      discipline: '',
      category: '',
      apparatus: [],
      selectedClubs: [],
      selectedJudges: [],
      selectedLevels: [],
      imageFile: null, 
      imagePreview: null 
    });
  };

  const cancelClubForm = () => {
    setShowClubForm(false);
    setEditingClub(null);
    setEditingGymnast(null);
    setClubForm({ 
      name: '',
      location: '', 
      gymnasts: [], 
      imageFile: null, 
      imagePreview: null 
    });
    setNewGymnastForm({ 
      name: '', 
      level: '', 
      franja: '', 
      imageFile: null, 
      imagePreview: null 
    });
  };

  // Función para abrir modal de búsqueda de gimnastas
  const openGymnastSearch = () => {
    setShowGymnastModal(true);
    loadEventGymnasts();
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // Determinar si la ruta actual requiere autenticación
  const currentPath = location.pathname;
  const requiresAuth = currentPath.includes('/admin') || currentPath.includes('/jueces');
  
  // Si se muestra el registro de juez, mostrar ese formulario
  if (showJudgeRegistration) {
    return (
      <div className="app-container">
        <JudgeRegistration onRegistrationSuccess={handleRegistrationSuccess} />
      </div>
    );
  }
  
  // Si el usuario no está autenticado y la ruta requiere autenticación
  if (requiresAuth && (!auth.role || auth.role === AUTH_ROLES.PUBLIC)) {
    // Mostrar solo el formulario de login, sin selector de evento
    return (
      <div className="app-container">
        <LoginNew 
          onLoginSuccess={handleLoginSuccess}
          onShowJudgeRegistration={handleShowJudgeRegistration}
        />
      </div>
    );
  }

  // Si está en /resultados (vista pública), mostrar directamente sin requerir login
  // Si no está autenticado y NO está en una ruta protegida ni en resultados, mostrar login
  if ((!auth.role || auth.role === AUTH_ROLES.PUBLIC) && !currentPath.startsWith('/resultados') && !requiresAuth) {
    return (
      <div className="app-container">
        <LoginNew 
          onLoginSuccess={handleLoginSuccess}
          onShowJudgeRegistration={handleShowJudgeRegistration}
        />
      </div>
    );
  }

  // Para la vista pública, usar un rol temporal si no tiene uno
  const renderAuth = (currentPath.startsWith('/resultados') && (!auth.role || auth.role === AUTH_ROLES.PUBLIC))
    ? { role: AUTH_ROLES.PUBLIC, user: null }
    : auth;

  return (
    <div className="app-container">
      <Header
        view={view}
        setView={setView}
        auth={renderAuth}
        onLogout={handleLogout}
        onChangeProfile={handleChangeProfile}
        allowedViews={
          renderAuth.role === AUTH_ROLES.ADMIN 
            ? [VIEWS.ADMIN, VIEWS.PUBLIC]
            : renderAuth.role === AUTH_ROLES.JUDGE
            ? [VIEWS.SCORES, VIEWS.PUBLIC]
            : [VIEWS.PUBLIC]
        }
      />

      <div className="app-layout">
        {renderAuth?.role && renderAuth.role !== AUTH_ROLES.PUBLIC && (
          <Sidebar
            view={view}
            setView={setView}
            auth={renderAuth}
            onLogout={handleLogout}
            allowedViews={
              renderAuth.role === AUTH_ROLES.ADMIN 
                ? [VIEWS.ADMIN, VIEWS.CLUBS, VIEWS.JUDGES, VIEWS.PUBLIC]
                : renderAuth.role === AUTH_ROLES.JUDGE
                ? [VIEWS.SCORES, VIEWS.PUBLIC]
                : [VIEWS.PUBLIC]
            }
          />
        )}

        <div className={`main-content${!renderAuth?.role || renderAuth.role === AUTH_ROLES.PUBLIC ? ' no-sidebar' : ''}`}>
          {view === VIEWS.PUBLIC && (
            <PublicView
              events={events}
              selectedEvent={selectedEvent}
              setSelectedEvent={handleSelectEvent}
              currentEvent={currentEvent}
              currentClubs={currentClubs}
              LEVEL_OPTIONS={LEVEL_OPTIONS}
              FRANJA_OPTIONS={FRANJA_OPTIONS}
              APPARATUS_OPTIONS={APPARATUS_OPTIONS}
            />
          )}

          {view === VIEWS.SCORES && (
            <ScoreEntryView
              currentEvent={currentEvent}
              currentClubs={currentClubs}
              clubs={clubs}
              updateScore={updateScore}
              loadClubGymnasts={loadClubGymnasts}
              gymnasts={gymnasts}
              LEVEL_OPTIONS={LEVEL_OPTIONS}
              FRANJA_OPTIONS={FRANJA_OPTIONS}
              APPARATUS_OPTIONS={APPARATUS_OPTIONS}
              events={events}
              auth={auth}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
            />
          )}

          {view === VIEWS.CLUBS && (
            <ClubsView
              clubs={clubs}
              showClubForm={showClubForm}
              setShowClubForm={setShowClubForm}
              editingClub={editingClub}
              setEditingClub={setEditingClub}
              deleteClub={deleteClub}
              editClub={editClub}
              expandedClubs={expandedClubs}
              toggleExpandClub={toggleExpandClub}
              clubForm={clubForm}
              setClubForm={setClubForm}
              addClub={addClub}
              cancelClubForm={cancelClubForm}
              uploadingImage={uploadingImage}
              handleFileChange={handleFileChange}
              clearImagePreview={clearImagePreview}
              newGymnastForm={newGymnastForm}
              setNewGymnastForm={setNewGymnastForm}
              addGymnastToClubForm={addGymnastToClubForm}
              removeGymnastFromClubForm={removeGymnastFromClubForm}
              editGymnastInForm={editGymnastInForm}
              editingGymnast={editingGymnast}
              setEditingGymnast={setEditingGymnast}
              LEVEL_OPTIONS={LEVEL_OPTIONS}
              FRANJA_OPTIONS={FRANJA_OPTIONS}
              openGymnastSearch={openGymnastSearch}
            />
          )}

          {view === VIEWS.JUDGES && (
            <JudgesView
              judges={judges}
            />
          )}

          {view === VIEWS.ANALYTICS && (
            <AnalyticsView
              events={events}
              clubs={clubs}
              gymnasts={gymnasts}
              scores={scores}
              selectedClub={selectedClub}
              setSelectedClub={setSelectedClub}
            />
          )}

          {view === VIEWS.ADMIN && (
              <AdminView
                  events={events}
                  showEventForm={showEventForm}
                  setShowEventForm={setShowEventForm}
                  editingEvent={editingEvent}
                  setEditingEvent={setEditingEvent}
                  eventForm={eventForm}
                  setEventForm={setEventForm}
                  addEvent={addEvent}
                  deleteEvent={deleteEvent}
                  editEvent={editEvent}
                  uploadingImage={uploadingImage}
                  handleFileChange={handleFileChange}
                  clearImagePreview={clearImagePreview}
                  cancelEventForm={cancelEventForm}
                  clubs={clubs}
                  judges={judges}
                  auth={auth}
              />
          )}
          </div>
      </div>
      <CategoryFooter />
    </div>
  );
};

export default GymnasticsScoresApp;